import React, { useState } from 'react'
import { NavLink, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBilling } from '../contexts/BillingContext'
import ProfileProgress from './ProfileProgress'
import {
  House,
  Kanban,
  ChartBar,
  FolderOpen,
  SignOut,
  List,
  CalendarBlank,
  UserCircle,
  Scroll,
  ArrowUpRight,
  YoutubeLogo,
  LinkedinLogo,
} from '@phosphor-icons/react'

const navItems = [
  { to: '/', icon: House, label: 'Vídeos', exact: true },
  { to: '/kanban', icon: Kanban, label: 'Pipeline' },
  { to: '/scripts', icon: Scroll, label: 'Guiones' },
  { to: '/calendar', icon: CalendarBlank, label: 'Calendario' },
  { to: '/analysis', icon: ChartBar, label: 'Análisis' },
  { to: '/assets', icon: FolderOpen, label: 'Biblioteca' },
]

const integrationItems = [
  { to: '/youtube', icon: YoutubeLogo, label: 'YouTube', color: '#FF0000' },
  { to: '/linkedin', icon: LinkedinLogo, label: 'LinkedIn', color: '#0077B5' },
]

export default function Layout({ children }) {
  const { user, logout } = useAuth()
  const { plan, isFreePlan, openPortal } = useBilling()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/auth')
  }

  const initials = user?.name
    ? user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'CC'

  return (
    <div className="flex h-screen bg-ds-bg overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col bg-ds-surface border-r border-ds-border h-full">
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-ds-border">
          <div
            className="w-7 h-7 bg-ds-red flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: 2 }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M1.5 3.2L6.5 1L11.5 3.2V6.5C11.5 9.5 6.5 12 6.5 12C6.5 12 1.5 9.5 1.5 6.5V3.2Z"
                fill="white"
                opacity="0.9"
              />
            </svg>
          </div>
          <span className="type-label text-ds-text tracking-[0.04em]">
            RDK<span className="text-ds-red">·</span>CC
          </span>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map(({ to, icon: Icon, label, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Icon size={16} weight="duotone" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Integrations */}
        <div className="px-3 pb-2 border-t border-ds-border pt-3">
          <p className="px-3 mb-1 text-[10px] text-ds-faint uppercase tracking-widest font-medium">
            Integraciones
          </p>
          {integrationItems.map(({ to, icon: Icon, label, color }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={16} weight="fill" style={{ color }} />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>

        {/* User section */}
        <div className="px-3 pb-4 border-t border-ds-border pt-4 space-y-0.5">
          {/* Plan badge / upgrade CTA */}
          {isFreePlan ? (
            <Link
              to="/pricing"
              className="flex items-center justify-between px-3 py-2 bg-[rgba(212,43,30,0.08)] border border-[rgba(212,43,30,0.2)] hover:bg-[rgba(212,43,30,0.12)] transition-colors mb-2 no-underline"
              style={{ borderRadius: 2 }}
            >
              <div>
                <p className="type-caption text-ds-red">Plan Free</p>
                <p
                  className="text-[10px] text-ds-muted mt-0.5"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  Actualizar a Pro
                </p>
              </div>
              <ArrowUpRight size={14} className="text-ds-red" />
            </Link>
          ) : (
            <button
              onClick={openPortal}
              className="flex items-center justify-between w-full px-3 py-2 bg-ds-elevated hover:bg-[#222] transition-colors mb-2"
              style={{ borderRadius: 2 }}
            >
              <div>
                <p className="type-caption text-ds-text capitalize">{plan}</p>
                <p
                  className="text-[10px] text-ds-muted mt-0.5"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  Gestionar suscripción
                </p>
              </div>
              <ArrowUpRight size={14} className="text-ds-muted" />
            </button>
          )}
          <Link
            to="/profile"
            className="flex items-center gap-3 px-3 py-2 hover:bg-ds-elevated transition-colors group no-underline"
            style={{ borderRadius: 2 }}
          >
            <div
              className="w-7 h-7 bg-ds-red flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 type-caption"
              style={{ borderRadius: 2 }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-ds-text text-[13px] font-medium truncate"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                {user?.name}
              </p>
              <p
                className="text-ds-muted text-[11px] truncate group-hover:text-ds-text transition-colors"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                Ver perfil
              </p>
            </div>
            <UserCircle
              size={14}
              className="text-ds-faint group-hover:text-ds-muted transition-colors flex-shrink-0"
            />
          </Link>
          <button
            onClick={handleLogout}
            className="nav-item w-full text-left hover:text-ds-red"
            data-testid="logout-btn"
          >
            <SignOut size={16} weight="duotone" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-ds-surface border-b border-ds-border">
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 bg-ds-red flex items-center justify-center"
              style={{ borderRadius: 2 }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  d="M1.5 3.2L6.5 1L11.5 3.2V6.5C11.5 9.5 6.5 12 6.5 12C6.5 12 1.5 9.5 1.5 6.5V3.2Z"
                  fill="white"
                  opacity="0.9"
                />
              </svg>
            </div>
            <span className="type-label text-ds-text tracking-[0.04em]">
              RDK<span className="text-ds-red">·</span>CC
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-ds-muted p-1"
            data-testid="mobile-menu-btn"
          >
            <List size={22} />
          </button>
        </header>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-ds-surface border-b border-ds-border px-3 py-2">
            {navItems.map(({ to, icon: Icon, label, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={16} weight="duotone" />
                <span>{label}</span>
              </NavLink>
            ))}
            <button
              onClick={handleLogout}
              className="nav-item w-full text-left hover:text-ds-red"
              data-testid="mobile-logout-btn"
            >
              <SignOut size={16} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        )}

        {/* El perfil se completa desde dentro, no antes de entrar */}
        <ProfileProgress />

        {/* Page content */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Mobile Bottom Tab Bar */}
        <nav className="md:hidden flex bg-ds-surface border-t border-ds-border pb-safe">
          {navItems.map(({ to, icon: Icon, label, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center py-2 gap-0.5 transition-colors ${isActive ? 'text-ds-red' : 'text-ds-muted'}`
              }
              data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Icon size={20} weight="duotone" />
              <span className="type-caption text-[9px]">{label}</span>
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  )
}
